import PropTypes from 'prop-types';
import {
  FilledInput,
  FormControl,
  FormHelperText,
  InputLabel,
  InputAdornment,
} from '@mui/material';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { handleKeyDownNumber } from '@/shared/utils/common/handleKeyDownNumber';
import { customInputDesignProperties } from '@/shared/utils/common/customInputDesignProperties';
import { Tooltip } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';

type Props<T extends FieldValues> = {
  name: Path<T>;
  label?: string;
  id?: string;
  register?: object;
  error?: boolean;
  errorMessage?: string;
  required: boolean;
  type?: string;
  style?: React.CSSProperties | null;
  step?: string;
  disabled?: boolean;
  control?: Control<T>;
  placeholder?: string;
  tooltipProps?: TooltipProps;
  endAdornment?: string;
};

type TooltipProps = {
  title: string;
  color?: string;
  trigger?: ActionType | ActionType[];
  bodyclassName?: string;
  placement?: TooltipPlacement;
};

type ActionType = 'click' | 'hover' | 'focus' | 'contextMenu';

export const CustomInput = <T extends FieldValues>({
  name,
  label,
  id,
  error,
  errorMessage,
  required,
  type,
  style,
  step,
  disabled,
  control,
  placeholder,
  tooltipProps,
  endAdornment,
}: Props<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl variant="filled">
          {type === 'date' ? (
            <InputLabel
              required={required}
              style={{
                color: '#3f3f3f',
                fontSize: '16px',
                fontWeight: 'bolder',
              }}
              shrink
              htmlFor={id}
            >
              {label}
            </InputLabel>
          ) : (
            <InputLabel
              required={required}
              style={{
                color: '#3f3f3f',
                fontSize: '16px',
                fontWeight: 'bolder',
              }}
              htmlFor={id}
            >
              {label}
            </InputLabel>
          )}

          <Tooltip
            title={!error ? tooltipProps?.title : null}
            color={tooltipProps?.color}
            trigger={tooltipProps?.trigger}
            classNames={{
              body: tooltipProps?.bodyclassName,
            }}
            placement={tooltipProps?.placement}
          >
            <FilledInput
              id={id}
              slotProps={step ? { input: { step: step } } : {}}
              {...field}
              style={{
                ...style,
                backgroundColor: style?.backgroundColor ?? 'white',
                border: `1px solid ${error ? 'red' : '#c4c4c4'}`,
              }}
              error={error}
              type={type === 'number' ? 'text' : type}
              name={field.name}
              value={field.value === '' ? '' : field.value}
              sx={{ ...customInputDesignProperties }}
              disabled={disabled}
              onKeyDown={
                type === 'number'
                  ? (event: React.KeyboardEvent) => handleKeyDownNumber(event)
                  : undefined
              }
              endAdornment={
                <InputAdornment position="end">
                  <span style={{ fontSize: '12px' }}>{endAdornment}</span>
                </InputAdornment>
              }
              placeholder={placeholder}
            ></FilledInput>
          </Tooltip>

          {errorMessage && (
            <FormHelperText
              style={{ color: 'red', margin: '0', maxWidth: '260px' }}
            >
              {errorMessage}
            </FormHelperText>
          )}
        </FormControl>
      )}
    />
  );
};

CustomInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  id: PropTypes.string,
  register: PropTypes.object,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  required: PropTypes.bool.isRequired,
  type: PropTypes.string,
  style: PropTypes.object,
  step: PropTypes.string,
  disabled: PropTypes.bool,
  control: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
};
